import { Form } from '@remix-run/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import cx from 'classnames';
import type { FormEventHandler } from 'react';
import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import Button from '~/components/Button';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';

import useCreateStripePriceSession from '~/hooks/useCreateStripePriceSession';

import EmailCaptureModal from './PlanCards/components/EmailCaptureModal';

interface PayFormProps {
  priceId: string;
  onContinue: () => void;
  cardRequired?: boolean;
  text?: string | React.ReactNode;
  buttonStyle?: React.CSSProperties;
  fontFamily?: string;
  premiumOfferId?: string;
  isCollectingBillingAddress?: boolean;
  email: string;
}

const PayForm = ({
  priceId,
  cardRequired = true,
  text,
  onContinue,
  buttonStyle,
  fontFamily,
  premiumOfferId = '',
  isCollectingBillingAddress = false,
  email = '',
}: PayFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { classes } = useWebThemeContext();
  const redirectToStripe = cardRequired;
  const [isEmailCaptureModalOpen, setIsEmailCaptureModalOpen] = React.useState(false);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { mutateAsync: createStripePriceSession } = useCreateStripePriceSession();

  const handleStripeSubmit = async (_event: any) => {
    if (!stripe || !elements) {
      return;
    }

    try {
      setIsSubmitting(true);

      const stripeSessionId = await createStripePriceSession(
        priceId,
        email,
        premiumOfferId,
        isCollectingBillingAddress,
      );

      if (stripeSessionId) {
        const response = await stripe.redirectToCheckout({
          sessionId: stripeSessionId,
        });
        if (!response.error) {
          return; // all good
        }
      }
    } catch (error) {
      console.error(error);
    }
    // if we reach here, something went wrong
    toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
    setIsSubmitting(false);
  };

  const handleSwarmSubmit = async (_event: any) => {
    setIsEmailCaptureModalOpen(true);
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    if (redirectToStripe) {
      handleStripeSubmit(event);
    } else {
      handleSwarmSubmit(event);
    }
  };

  return (
    <>
      <Form method="post" onSubmit={handleSubmit}>
        <Button
          type="submit"
          variant="primary"
          className={cx('flex w-full justify-center', classes.radius, fontFamily)}
          disabled={isSubmitting}
          style={buttonStyle}
        >
          {text || t('shared.cta.get_access')}
        </Button>
      </Form>
      <EmailCaptureModal
        isOpen={isEmailCaptureModalOpen}
        onClose={() => {
          setIsEmailCaptureModalOpen(false);
        }}
        onConfirm={() => {
          setIsEmailCaptureModalOpen(false);
          onContinue();
        }}
        premiumOfferId={premiumOfferId}
      />
    </>
  );
};

export default PayForm;
